import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Competition, MyTeamCompetitions } from "../types/competition.types";
import { ToastMessage, useToast } from "../components/toast";
import { CompetitionsAPI } from "../APIs/competitions.api";
import { useAuth } from "./AuthContext";
import { UserRoles } from "../types/user.types";

interface ICompetitionsContextProps {
  availableCompetitions: Competition[];
  registeredCompetitions: MyTeamCompetitions[];
  adminCompetitions: Competition[];
  isLoading: boolean;
  error: string | null;
  getCompetitions: () => Promise<void>;
  getAdminCompetitions: () => Promise<void>;
  handleError: (message: string) => void;
  addToast: (toast: ToastMessage) => void;
  currentCompetitionId: number | null;
  getCurrentCompetitionId: () => Promise<number | null>;
}

const CompetitionsContext = createContext<
  ICompetitionsContextProps | undefined
>(undefined);

let currentCompetitionIdCache: number | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1H

export const CompetitionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentCompetitionId, setCurrentCompetitionId] = useState<
    number | null
  >(null);
  const [availableCompetitions, setAvailableCompetitions] = useState<
    Competition[]
  >([]);
  const [registeredCompetitions, setRegisteredCompetitions] = useState<
    MyTeamCompetitions[]
  >([]);
  const [adminCompetitions, setAdminCompetitions] = useState<Competition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();
  const { user } = useAuth();

  const handleError = useCallback((message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  }, []);

  const getCurrentCompetitionId = useCallback(async (): Promise<
    number | null
  > => {
    if (
      currentCompetitionIdCache !== null &&
      cacheTimestamp &&
      Date.now() - cacheTimestamp < CACHE_DURATION
    ) {
      return currentCompetitionIdCache;
    }

    try {
      const response = await CompetitionsAPI.getCurrentCompetition();

      if (response?.statusCode === 200) {
        const competitionId = response.data.id;

        currentCompetitionIdCache = competitionId;
        cacheTimestamp = Date.now();

        return competitionId;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }, []);

  const getCompetitions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await CompetitionsAPI.getCompetitionsTeamMember();
      if (response?.statusCode === 200) {
        setAvailableCompetitions(response.data.availableCompetitions ?? []);
        setRegisteredCompetitions(response.data.registeredCompetitions ?? []);
      } else {
        handleError(response?.message || "Unknown error occurred");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? `Failed to fetch competitions: ${err.message}`
          : "Unexpected error while fetching competitions";
      handleError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const getAdminCompetitions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await CompetitionsAPI.getCompetitions();
      if (response?.statusCode === 200) {
        const sortedCompetitions = response.data.sort(
          (a: Competition, b: Competition) => {
            const dateA = new Date(a.start_date).getTime();
            const dateB = new Date(b.start_date).getTime();
            return dateB - dateA;
          }
        );

        setAdminCompetitions(sortedCompetitions);

        const topCompetition = sortedCompetitions[0];
        const competitionId = topCompetition?.id || null;
        setCurrentCompetitionId(competitionId);

        currentCompetitionIdCache = competitionId;
        cacheTimestamp = Date.now();
      } else {
        handleError(response?.message || "Error desconocido");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? `Error al obtener las competencias: ${err.message}`
          : "Error inesperado al obtener las competencias";
      handleError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    if (
      user?.roleId === UserRoles.CLIENT_ADMIN ||
      user?.roleId === UserRoles.SYSTEM_ADMIN
    ) {
      const initializeCurrentCompetition = async () => {
        const competitionId = await getCurrentCompetitionId();
        setCurrentCompetitionId(competitionId);
      };

      initializeCurrentCompetition();
    }
  }, [getCurrentCompetitionId, user?.roleId]);

  return (
    <CompetitionsContext.Provider
      value={{
        availableCompetitions,
        registeredCompetitions,
        adminCompetitions,
        isLoading,
        error,
        getCompetitions,
        getAdminCompetitions,
        handleError,
        addToast,
        currentCompetitionId,
        getCurrentCompetitionId,
      }}
    >
      {children}
    </CompetitionsContext.Provider>
  );
};

export const useCompetitions = () => {
  const context = useContext(CompetitionsContext);
  if (!context) {
    throw new Error(
      "useCompetitions must be used within a CompetitionsProvider"
    );
  }
  return context;
};

export const getCurrentCompetitionIdGlobal = async (): Promise<
  number | null
> => {
  if (
    currentCompetitionIdCache !== null &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return currentCompetitionIdCache;
  }

  try {
    const response = await CompetitionsAPI.getCurrentCompetition();

    if (response?.statusCode === 200) {
      const competitionId = response.data.id;

      currentCompetitionIdCache = competitionId;
      cacheTimestamp = Date.now();

      return competitionId;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};
